import { Shape, WallShape, WallPathShape, CircleShape, SpotShape } from '../types';
import { getEffectiveSpotAppearance } from './spots';

const SNAP_THRESHOLD = 12;

export interface SnapResult {
  position: { x?: number; y?: number };
  lines: { x?: number; y?: number };
  /** Target dot: the point on the target shape where the snap line should show a marker */
  targetDot: { x?: number; y?: number };
  /** Set when the active shape's centroid is aligning with the centroid
   *  of another shape on BOTH axes AND the active shape's bbox fits
   *  inside that shape. Renders a distinct "centered inside" indicator
   *  so the user can confidently tell when an item is truly centered
   *  within another (e.g. a spot inside a rectangle). */
  centeredIn?: BoundsRect;
  /** Active shape's bbox at the snapped position. Drawn as a translucent
   *  outline in the centered indicator so the user can confirm visually
   *  that the active shape sits inside the target. */
  activeBounds?: BoundsRect;
}

const SPOT_RECT_WIDTH = 90;
const SPOT_RECT_HEIGHT = 50;

interface EdgePoints {
  left: number;
  right: number;
  center: number;
  top: number;
  bottom: number;
  middle: number;
}

const getSpotEdgePoints = (
  spot: SpotShape,
  position: { x: number; y: number }
): EdgePoints => {
  const spotType = getEffectiveSpotAppearance(spot.spotType, spot.spotAppearance);

  if (spotType === 'circle') {
    return {
      left: position.x,
      right: position.x + 80,
      center: position.x + 40,
      top: position.y,
      bottom: position.y + 80,
      middle: position.y + 40,
    };
  }

  if (spotType === 'meeting_room') {
    return {
      left: position.x,
      right: position.x + 140,
      center: position.x + 70,
      top: position.y,
      bottom: position.y + 120,
      middle: position.y + 60,
    };
  }

  if (spotType === 'desk') {
    const isVertical = spot.orientation === 'left' || spot.orientation === 'right';
    if (!isVertical) {
      const rectY = spot.orientation === 'top' ? 40 : 10;
      return {
        left: position.x,
        right: position.x + SPOT_RECT_WIDTH,
        center: position.x + SPOT_RECT_WIDTH / 2,
        top: position.y + rectY,
        bottom: position.y + rectY + SPOT_RECT_HEIGHT,
        middle: position.y + rectY + SPOT_RECT_HEIGHT / 2,
      };
    }
    if (spot.orientation === 'left') {
      return {
        left: position.x + SPOT_RECT_WIDTH - SPOT_RECT_HEIGHT,
        right: position.x + SPOT_RECT_WIDTH,
        center: position.x + SPOT_RECT_WIDTH - SPOT_RECT_HEIGHT / 2,
        top: position.y,
        bottom: position.y + SPOT_RECT_WIDTH,
        middle: position.y + SPOT_RECT_WIDTH / 2,
      };
    }
    return {
      left: position.x,
      right: position.x + SPOT_RECT_HEIGHT,
      center: position.x + SPOT_RECT_HEIGHT / 2,
      top: position.y,
      bottom: position.y + SPOT_RECT_WIDTH,
      middle: position.y + SPOT_RECT_WIDTH / 2,
    };
  }

  return {
    left: position.x,
    right: position.x + SPOT_RECT_WIDTH,
    center: position.x + SPOT_RECT_WIDTH / 2,
    top: position.y,
    bottom: position.y + spot.height,
    middle: position.y + spot.height / 2,
  };
};

/** Center Y of a shape's bounding box (where the red dot lands on a vertical snap line). */
const getShapeCenterY = (shape: Shape): number => {
  const bounds = getShapeBounds(shape);
  return (bounds.top + bounds.bottom) / 2;
};

/** Center X of a shape's bounding box (where the red dot lands on a horizontal snap line). */
const getShapeCenterX = (shape: Shape): number => {
  const bounds = getShapeBounds(shape);
  return (bounds.left + bounds.right) / 2;
};

interface SnapPointWithShape {
  x?: number;
  y?: number;
  label: string;
  shape: Shape;
}

export interface BoundsRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface NeighborDistance {
  /** Dimension line endpoints in world coords. */
  from: { x: number; y: number };
  to: { x: number; y: number };
  /** World-unit gap between active and neighbor edges. */
  value: number;
  /** Which way the gap is measured — used to position the label. */
  axis: 'horizontal' | 'vertical';
}

/** Closest neighbor edge-to-edge distance in each cardinal direction.
 *  Only neighbors that perpendicularly overlap with the active shape
 *  count, so a desk row to the side doesn't claim "the closest shape
 *  above" — that would be misleading visually. The dimension line is
 *  drawn through the middle of the overlap so it stays inside both
 *  rectangles. */
export const computeNeighborDistances = (
  activeBounds: BoundsRect,
  shapes: Shape[],
  skipIds: Set<string>
): NeighborDistance[] => {
  const candidates = shapes
    .filter(s => !skipIds.has(s.id))
    .map(s => ({ shape: s, bounds: getShapeBounds(s) }));

  const xOverlap = (b: BoundsRect) =>
    b.right > activeBounds.left && b.left < activeBounds.right;
  const yOverlap = (b: BoundsRect) =>
    b.bottom > activeBounds.top && b.top < activeBounds.bottom;

  const results: NeighborDistance[] = [];

  type Best = { gap: number; bounds: BoundsRect } | null;
  let top: Best = null;
  let bottom: Best = null;
  let left: Best = null;
  let right: Best = null;

  for (const c of candidates) {
    const b = c.bounds;
    if (b.bottom <= activeBounds.top && xOverlap(b)) {
      const gap = activeBounds.top - b.bottom;
      if (!top || gap < top.gap) top = { gap, bounds: b };
    }
    if (b.top >= activeBounds.bottom && xOverlap(b)) {
      const gap = b.top - activeBounds.bottom;
      if (!bottom || gap < bottom.gap) bottom = { gap, bounds: b };
    }
    if (b.right <= activeBounds.left && yOverlap(b)) {
      const gap = activeBounds.left - b.right;
      if (!left || gap < left.gap) left = { gap, bounds: b };
    }
    if (b.left >= activeBounds.right && yOverlap(b)) {
      const gap = b.left - activeBounds.right;
      if (!right || gap < right.gap) right = { gap, bounds: b };
    }
  }

  if (top) {
    const xMin = Math.max(activeBounds.left, top.bounds.left);
    const xMax = Math.min(activeBounds.right, top.bounds.right);
    const x = (xMin + xMax) / 2;
    results.push({
      from: { x, y: top.bounds.bottom },
      to: { x, y: activeBounds.top },
      value: top.gap,
      axis: 'vertical'
    });
  }
  if (bottom) {
    const xMin = Math.max(activeBounds.left, bottom.bounds.left);
    const xMax = Math.min(activeBounds.right, bottom.bounds.right);
    const x = (xMin + xMax) / 2;
    results.push({
      from: { x, y: activeBounds.bottom },
      to: { x, y: bottom.bounds.top },
      value: bottom.gap,
      axis: 'vertical'
    });
  }
  if (left) {
    const yMin = Math.max(activeBounds.top, left.bounds.top);
    const yMax = Math.min(activeBounds.bottom, left.bounds.bottom);
    const y = (yMin + yMax) / 2;
    results.push({
      from: { x: left.bounds.right, y },
      to: { x: activeBounds.left, y },
      value: left.gap,
      axis: 'horizontal'
    });
  }
  if (right) {
    const yMin = Math.max(activeBounds.top, right.bounds.top);
    const yMax = Math.min(activeBounds.bottom, right.bounds.bottom);
    const y = (yMin + yMax) / 2;
    results.push({
      from: { x: activeBounds.right, y },
      to: { x: right.bounds.left, y },
      value: right.gap,
      axis: 'horizontal'
    });
  }

  return results;
};

/** Classify a spot's orientation as horizontal (top/bottom — desk is
 *  wider than tall) or vertical (left/right — desk is taller than wide).
 *  Used to suppress one snap axis when two same-class spots are being
 *  aligned: a horizontal row of desks should snap Y (so they line up at
 *  the same height) but leave X free, since the gap between adjacent
 *  desks is what the user sets manually (with the precision-mode
 *  measurement overlay). */
type SpotOrientationClass = 'horizontal' | 'vertical' | undefined;

const getSpotOrientationClass = (
  shape: Shape
): SpotOrientationClass => {
  if (shape.type !== 'spot') return undefined;
  const spot = shape as SpotShape;
  if (spot.orientation === 'top' || spot.orientation === 'bottom') {
    return 'horizontal';
  }
  if (spot.orientation === 'left' || spot.orientation === 'right') {
    return 'vertical';
  }
  return undefined;
};

/** Expand bounds to include every companion shape. Companions store
 *  coordinates relative to the anchor (spot.x, spot.y). */
const expandBoundsWithCompanions = (
  bounds: BoundsRect,
  companions: Shape[] | undefined,
  anchor: { x: number; y: number }
): BoundsRect => {
  if (!companions?.length) return bounds;
  let { left, right, top, bottom } = bounds;
  for (const companion of companions) {
    const cBounds = getShapeBounds(companion);
    left = Math.min(left, anchor.x + cBounds.left);
    right = Math.max(right, anchor.x + cBounds.right);
    top = Math.min(top, anchor.y + cBounds.top);
    bottom = Math.max(bottom, anchor.y + cBounds.bottom);
  }
  return { left, right, top, bottom };
};

/** Axis-aligned bounding box for any shape in stored coordinates. */
export const getShapeBounds = (shape: Shape): BoundsRect => {
  if (shape.type === 'wall') {
    const wall = shape as WallShape;
    return {
      left: Math.min(wall.x, wall.x2),
      right: Math.max(wall.x, wall.x2),
      top: Math.min(wall.y, wall.y2),
      bottom: Math.max(wall.y, wall.y2),
    };
  }
  if (shape.type === 'wall-path') {
    const path = shape as WallPathShape;
    if (path.vertices.length === 0) {
      return { left: shape.x, right: shape.x, top: shape.y, bottom: shape.y };
    }
    const xs = path.vertices.map(v => v.x);
    const ys = path.vertices.map(v => v.y);
    return {
      left: Math.min(...xs),
      right: Math.max(...xs),
      top: Math.min(...ys),
      bottom: Math.max(...ys),
    };
  }
  if (shape.type === 'circle') {
    const c = shape as CircleShape;
    return {
      left: c.x - c.width / 2,
      right: c.x + c.width / 2,
      top: c.y - c.height / 2,
      bottom: c.y + c.height / 2,
    };
  }
  if (shape.type === 'spot') {
    const spot = shape as SpotShape;
    const edges = getSpotEdgePoints(spot, { x: spot.x, y: spot.y });
    const base: BoundsRect = {
      left: edges.left,
      right: edges.right,
      top: edges.top,
      bottom: edges.bottom,
    };
    return expandBoundsWithCompanions(base, spot.companions, { x: spot.x, y: spot.y });
  }
  return {
    left: shape.x,
    right: shape.x + shape.width,
    top: shape.y,
    bottom: shape.y + shape.height,
  };
};

/** Spot edge points expanded to include companions (composite templates). */
const getSpotCompositeEdges = (
  spot: SpotShape,
  position: { x: number; y: number }
): EdgePoints => {
  const base = getSpotEdgePoints(spot, position);
  if (!spot.companions?.length) return base;
  const baseBounds: BoundsRect = {
    left: base.left,
    right: base.right,
    top: base.top,
    bottom: base.bottom,
  };
  const expanded = expandBoundsWithCompanions(baseBounds, spot.companions, position);
  return {
    left: expanded.left,
    right: expanded.right,
    center: (expanded.left + expanded.right) / 2,
    top: expanded.top,
    bottom: expanded.bottom,
    middle: (expanded.top + expanded.bottom) / 2,
  };
};

interface ProximityBounds extends BoundsRect {
  /** World-unit margin around the active shape's bbox. Shapes whose own
   *  bbox doesn't overlap (active ± margin) get pruned from snap
   *  candidates — without this, every shape on the map contributes 6
   *  snap points and far-away alignments fire constantly. */
  margin: number;
}

const collectSnapPoints = (
  shapes: Shape[],
  skipIds: Set<string>,
  proximity?: ProximityBounds
): SnapPointWithShape[] => {
  return shapes
    .filter(shape => !skipIds.has(shape.id))
    .filter(shape => {
      if (!proximity) return true;
      const b = getShapeBounds(shape);
      const m = proximity.margin;
      if (b.right < proximity.left - m) return false;
      if (b.left > proximity.right + m) return false;
      if (b.bottom < proximity.top - m) return false;
      if (b.top > proximity.bottom + m) return false;
      return true;
    })
    .flatMap(shape => {
      const points: SnapPointWithShape[] = [];

      if (shape.type === 'wall') {
        const wall = shape as WallShape;
        points.push(
          { x: wall.x, label: 'start', shape },
          { x: wall.x2, label: 'end', shape },
          { x: (wall.x + wall.x2) / 2, label: 'center', shape },
          { y: wall.y, label: 'start', shape },
          { y: wall.y2, label: 'end', shape },
          { y: (wall.y + wall.y2) / 2, label: 'middle', shape },
        );
      } else if (shape.type === 'wall-path') {
        const path = shape as WallPathShape;
        for (const v of path.vertices) {
          points.push(
            { x: v.x, label: 'vertex', shape },
            { y: v.y, label: 'vertex', shape },
          );
        }
        // Bbox center/middle so shapes can align to the path's middle.
        if (path.vertices.length > 0) {
          const xs = path.vertices.map(v => v.x);
          const ys = path.vertices.map(v => v.y);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          const minY = Math.min(...ys);
          const maxY = Math.max(...ys);
          points.push(
            { x: (minX + maxX) / 2, label: 'center', shape },
            { y: (minY + maxY) / 2, label: 'middle', shape },
          );
        }
      } else if (shape.type === 'circle') {
        const circle = shape as CircleShape;
        points.push(
          { x: circle.x - circle.width / 2, label: 'left', shape },
          { x: circle.x + circle.width / 2, label: 'right', shape },
          { x: circle.x, label: 'center', shape },
        );
        points.push(
          { y: circle.y - circle.height / 2, label: 'top', shape },
          { y: circle.y + circle.height / 2, label: 'bottom', shape },
          { y: circle.y, label: 'middle', shape },
        );
      } else if (shape.type === 'spot') {
        const spot = shape as SpotShape;
        const edges = getSpotCompositeEdges(spot, { x: spot.x, y: spot.y });
        // Outer composite edges so other shapes can align to the full footprint.
        points.push(
          { x: edges.left, label: 'left', shape },
          { x: edges.right, label: 'right', shape },
          { x: edges.center, label: 'center', shape },
        );
        points.push(
          { y: edges.top, label: 'top', shape },
          { y: edges.bottom, label: 'bottom', shape },
          { y: edges.middle, label: 'middle', shape },
        );
        // Each companion's own edges so shapes can align to individual
        // components inside a composite template.
        if (spot.companions?.length) {
          for (const companion of spot.companions) {
            const cBounds = getShapeBounds(companion);
            const cLeft = spot.x + cBounds.left;
            const cRight = spot.x + cBounds.right;
            const cTop = spot.y + cBounds.top;
            const cBottom = spot.y + cBounds.bottom;
            points.push(
              { x: cLeft, label: 'companion-left', shape },
              { x: cRight, label: 'companion-right', shape },
              { x: (cLeft + cRight) / 2, label: 'companion-center', shape },
              { y: cTop, label: 'companion-top', shape },
              { y: cBottom, label: 'companion-bottom', shape },
              { y: (cTop + cBottom) / 2, label: 'companion-middle', shape },
            );
          }
        }
      } else {
        points.push(
          { x: shape.x, label: 'left', shape },
          { x: shape.x + shape.width, label: 'right', shape },
          { x: shape.x + shape.width / 2, label: 'center', shape },
        );
        points.push(
          { y: shape.y, label: 'top', shape },
          { y: shape.y + shape.height, label: 'bottom', shape },
          { y: shape.y + shape.height / 2, label: 'middle', shape },
        );
      }

      return points;
    });
};

export interface GroupSnapResult {
  /** Delta to apply on top of the drag so the group lands on the snap line */
  delta: { x?: number; y?: number };
  lines: { x?: number; y?: number };
  targetDot: { x?: number; y?: number };
}

/**
 * Snaps a group of shapes (represented by their combined axis-aligned bounds)
 * against other shapes. Returns the correction delta to apply on top of the
 * current drag translation so the whole group aligns with the nearest edge.
 */
export const getGroupSnapPosition = (
  groupBounds: BoundsRect,
  otherShapes: Shape[],
  scale: number = 1
): GroupSnapResult => {
  const result: GroupSnapResult = {
    delta: {},
    lines: {},
    targetDot: {},
  };

  const threshold = SNAP_THRESHOLD / Math.max(scale, 0.1);

  const activePoints: EdgePoints = {
    left: groupBounds.left,
    right: groupBounds.right,
    center: (groupBounds.left + groupBounds.right) / 2,
    top: groupBounds.top,
    bottom: groupBounds.bottom,
    middle: (groupBounds.top + groupBounds.bottom) / 2,
  };

  const snapPoints = collectSnapPoints(otherShapes, new Set());

  let bestXDiff = threshold;
  let xTargetShape: Shape | undefined;
  (['left', 'right', 'center'] as const).forEach(edge => {
    const activeValue = activePoints[edge];
    for (const point of snapPoints) {
      if (point.x === undefined) continue;
      const diff = Math.abs(point.x - activeValue);
      if (diff < bestXDiff) {
        bestXDiff = diff;
        result.delta.x = point.x - activeValue;
        result.lines.x = point.x;
        xTargetShape = point.shape;
      }
    }
  });

  let bestYDiff = threshold;
  let yTargetShape: Shape | undefined;
  (['top', 'bottom', 'middle'] as const).forEach(edge => {
    const activeValue = activePoints[edge];
    for (const point of snapPoints) {
      if (point.y === undefined) continue;
      const diff = Math.abs(point.y - activeValue);
      if (diff < bestYDiff) {
        bestYDiff = diff;
        result.delta.y = point.y - activeValue;
        result.lines.y = point.y;
        yTargetShape = point.shape;
      }
    }
  });

  if (result.lines.x !== undefined && xTargetShape) {
    result.targetDot.y = getShapeCenterY(xTargetShape);
  }
  if (result.lines.y !== undefined && yTargetShape) {
    result.targetDot.x = getShapeCenterX(yTargetShape);
  }

  return result;
};

export const getSnapPosition = (
  activeShape: Shape,
  otherShapes: Shape[],
  position: { x: number; y: number },
  skipId?: string,
  scale: number = 1
): SnapResult => {
  const result: SnapResult = {
    position: {},
    lines: {},
    targetDot: {},
  };

  // Keep snap feel roughly constant in screen pixels across zoom levels.
  // Without this, snapping barely triggers when zoomed out and feels sticky when zoomed in.
  const threshold = SNAP_THRESHOLD / Math.max(scale, 0.1);

  const isWall = activeShape.type === 'wall';
  const isWallPath = activeShape.type === 'wall-path';
  const isSpot = activeShape.type === 'spot';
  const activeWall = isWall ? activeShape as WallShape : null;
  const activePoints = isWallPath ? {
    left: position.x,
    right: position.x,
    center: position.x,
    top: position.y,
    bottom: position.y,
    middle: position.y,
  } : isWall && activeWall ? {
    left: position.x,
    right: position.x + (activeWall.x2 - activeWall.x),
    center: position.x + (activeWall.x2 - activeWall.x) / 2,
    top: position.y,
    bottom: position.y + (activeWall.y2 - activeWall.y),
    middle: position.y + (activeWall.y2 - activeWall.y) / 2,
  } : isSpot ? getSpotCompositeEdges(activeShape as SpotShape, { x: position.x, y: position.y }) : {
    left: position.x,
    right: position.x + activeShape.width,
    center: position.x + activeShape.width / 2,
    top: position.y,
    bottom: position.y + activeShape.height,
    middle: position.y + activeShape.height / 2,
  };

  // Limit snap candidates to shapes near the active shape. Without this
  // every shape on a busy map contributes snap points and the matcher
  // fires alignment lines on items the user can't even see at the
  // current zoom level. 300 world-units feels permissive enough that
  // real intent-to-align with a neighbor still triggers reliably.
  const proximityMargin = 300;
  const proximity: ProximityBounds = {
    left: activePoints.left,
    right: activePoints.right,
    top: activePoints.top,
    bottom: activePoints.bottom,
    margin: proximityMargin
  };
  const snapPoints = collectSnapPoints(
    otherShapes,
    new Set(skipId ? [skipId] : []),
    proximity
  );

  // Orientation-aware suppression for matching spots: when the active
  // spot and the candidate target spot share the same orientation
  // class, suppress the snap axis EDGES (left/right or top/bottom) so
  // the user can freely set the gap with precision mode (Shift). We
  // keep CENTER snaps on that same axis though — those are intentional
  // alignment ("stack centers"), not edge proximity, and silently
  // removing them takes away the only way to deliberately center two
  // spots on top of each other.
  //   - horizontal class (orientation top/bottom): desks form a row, so
  //     X edges are gap controls. Y always snaps so they line up at the
  //     same height.
  //   - vertical class (orientation left/right): desks form a column;
  //     Y edges suppressed, X always snaps.
  // For non-spot targets (walls, rectangles, other spot orientations)
  // both axes snap normally on every edge.
  const activeClass = getSpotOrientationClass(activeShape);
  const isSamePairSpot = (point: SnapPointWithShape): boolean => {
    if (!activeClass) return false;
    return getSpotOrientationClass(point.shape) === activeClass;
  };

  // For horizontal-class active spots, X edges are suppressed only when
  // matching another horizontal spot. Y edges always snap. For vertical
  // class, the roles flip.
  const suppressXEdge = (point: SnapPointWithShape): boolean =>
    activeClass === 'horizontal' && isSamePairSpot(point);
  const suppressYEdge = (point: SnapPointWithShape): boolean =>
    activeClass === 'vertical' && isSamePairSpot(point);

  // Check for x-axis snapping — find the closest match
  let bestXDiff = threshold;
  let xTargetShape: Shape | undefined;
  let xActiveEdge: string | undefined;
  let xPointLabel: string | undefined;
  (['left', 'right', 'center'] as const).forEach(edge => {
    const activeValue = activePoints[edge];
    snapPoints
      .filter(point => {
        if (point.x === undefined) return false;
        // Edge↔edge X snap suppressed for matched-pair spots, but
        // center↔center stays so the user can still deliberately
        // align two spots' centroids on the X axis.
        if (suppressXEdge(point) && (edge !== 'center' || point.label !== 'center')) {
          return false;
        }
        return true;
      })
      .forEach(point => {
        const diff = Math.abs(point.x! - activeValue);
        if (diff < bestXDiff) {
          bestXDiff = diff;
          const snapX = point.x!;
          result.position.x = snapX - (activeValue - position.x);
          result.lines.x = snapX;
          xTargetShape = point.shape;
          xActiveEdge = edge;
          xPointLabel = point.label;
        }
      });
  });

  // Check for y-axis snapping — find the closest match
  let bestYDiff = threshold;
  let yTargetShape: Shape | undefined;
  let yActiveEdge: string | undefined;
  let yPointLabel: string | undefined;
  (['top', 'bottom', 'middle'] as const).forEach(edge => {
    const activeValue = activePoints[edge];
    snapPoints
      .filter(point => {
        if (point.y === undefined) return false;
        if (suppressYEdge(point) && (edge !== 'middle' || point.label !== 'middle')) {
          return false;
        }
        return true;
      })
      .forEach(point => {
        const diff = Math.abs(point.y! - activeValue);
        if (diff < bestYDiff) {
          bestYDiff = diff;
          const snapY = point.y!;
          result.position.y = snapY - (activeValue - position.y);
          result.lines.y = snapY;
          yTargetShape = point.shape;
          yActiveEdge = edge;
          yPointLabel = point.label;
        }
      });
  });

  // Build target dot: place it at the snap line position on the target shape
  // For vertical snap line (x), the dot sits at (snapX, target center Y)
  // For horizontal snap line (y), the dot sits at (target center X, snapY)
  if (result.lines.x !== undefined && xTargetShape) {
    result.targetDot.y = getShapeCenterY(xTargetShape);
  }
  if (result.lines.y !== undefined && yTargetShape) {
    result.targetDot.x = getShapeCenterX(yTargetShape);
  }

  // Centered-inside detection: both axes snapped center↔center on the
  // same shape AND the active shape's bbox fits inside that shape. The
  // "center" / "middle" point labels apply to bbox centroids of any
  // collected shape (rect, circle, spot, wall-path); companion-center is
  // intentionally excluded so we only flag containment by a full shape.
  const isCenterX = xActiveEdge === 'center' && xPointLabel === 'center';
  const isCenterY = yActiveEdge === 'middle' && yPointLabel === 'middle';
  if (
    isCenterX &&
    isCenterY &&
    xTargetShape &&
    xTargetShape === yTargetShape
  ) {
    // Compute active shape bounds at the snapped position so containment
    // is evaluated against where the shape actually lands, not its
    // pre-snap drag position.
    const dx =
      result.position.x !== undefined ? result.position.x - position.x : 0;
    const dy =
      result.position.y !== undefined ? result.position.y - position.y : 0;
    const activeBounds: BoundsRect = {
      left: activePoints.left + dx,
      right: activePoints.right + dx,
      top: activePoints.top + dy,
      bottom: activePoints.bottom + dy
    };
    const target = getShapeBounds(xTargetShape);
    if (
      activeBounds.left >= target.left &&
      activeBounds.right <= target.right &&
      activeBounds.top >= target.top &&
      activeBounds.bottom <= target.bottom
    ) {
      result.centeredIn = target;
      result.activeBounds = activeBounds;
    }
  }

  return result;
};

const WALL_SNAP_THRESHOLD = 15;

/** Interior hit on a wall / wall-path edge, used to attach a new wall mid-segment. */
export interface WallSegmentSnap {
  shapeId: string;
  /** Edge index within the host: always 0 for a legacy `wall`, otherwise the wall-path edge index. */
  edgeIndex: number;
  /** Position along the edge (0–1) of the snapped point. */
  t: number;
}

export interface WallSnapResult {
  x: number;
  y: number;
  snapped: boolean;
  alignX?: number;
  alignY?: number;
  /** Set when the point snapped onto the interior of an existing wall segment. */
  segment?: WallSegmentSnap;
}

export interface WallSnapOptions {
  /** Also snap onto the interior of straight wall segments (not just endpoints/vertices). */
  includeSegments?: boolean;
  /** Shape id excluded from segment-interior snapping (e.g. the path being drawn). */
  excludeSegmentShapeId?: string;
}

/**
 * Snaps a point to the nearest wall endpoint within the snap threshold.
 */
export const snapToWallEndpoint = (
  point: { x: number; y: number },
  shapes: Shape[],
  scale: number = 1,
  extraEndpoints?: { x: number; y: number }[],
  options?: WallSnapOptions
): WallSnapResult => {
  const threshold = WALL_SNAP_THRESHOLD / Math.max(scale, 0.1);
  const endpoints: { x: number; y: number }[] = [];

  for (const shape of shapes) {
    if (shape.type === 'wall') {
      const wall = shape as WallShape;
      endpoints.push({ x: wall.x, y: wall.y });
      endpoints.push({ x: wall.x2, y: wall.y2 });
    } else if (shape.type === 'wall-path') {
      const path = shape as WallPathShape;
      for (const v of path.vertices) {
        endpoints.push({ x: v.x, y: v.y });
      }
    }
  }

  if (extraEndpoints) {
    endpoints.push(...extraEndpoints);
  }

  // 1. Proximity snap: snap to nearest endpoint
  let closestDist = threshold;
  let snappedPoint: WallSnapResult = { ...point, snapped: false };

  for (const ep of endpoints) {
    const dx = ep.x - point.x;
    const dy = ep.y - point.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < closestDist) {
      closestDist = dist;
      snappedPoint = { x: ep.x, y: ep.y, snapped: true };
    }
  }

  if (snappedPoint.snapped) return snappedPoint;

  // 2. Segment interior snap: project onto straight wall segments so a new
  // wall can attach anywhere along an existing one (not just at vertices)
  if (options?.includeSegments) {
    const segmentSnap = snapToWallSegmentInterior(
      point,
      shapes,
      threshold,
      options.excludeSegmentShapeId
    );
    if (segmentSnap) return segmentSnap;
  }

  // 3. Axis alignment: snap X or Y independently
  let alignX: number | undefined;
  let alignY: number | undefined;
  let closestXDiff = threshold;
  let closestYDiff = threshold;

  for (const ep of endpoints) {
    const xDiff = Math.abs(ep.x - point.x);
    if (xDiff < closestXDiff) {
      closestXDiff = xDiff;
      alignX = ep.x;
    }
    const yDiff = Math.abs(ep.y - point.y);
    if (yDiff < closestYDiff) {
      closestYDiff = yDiff;
      alignY = ep.y;
    }
  }

  const hasAlignment = alignX !== undefined || alignY !== undefined;

  return {
    x: alignX ?? point.x,
    y: alignY ?? point.y,
    snapped: hasAlignment,
    alignX,
    alignY
  };
};

/**
 * Finds the closest point on the interior of a straight wall segment within
 * threshold. Curved edges are skipped (splitting them would change geometry).
 * Projections landing within threshold of a segment endpoint snap to that
 * endpoint instead, so attaching never creates a degenerate micro-edge.
 */
const snapToWallSegmentInterior = (
  point: { x: number; y: number },
  shapes: Shape[],
  threshold: number,
  excludeShapeId?: string
): WallSnapResult | null => {
  let best: WallSnapResult | null = null;
  let bestDist = threshold;

  const considerSegment = (
    from: { x: number; y: number },
    to: { x: number; y: number },
    shapeId: string,
    edgeIndex: number
  ) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq === 0) return;

    const t = Math.max(
      0,
      Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSq)
    );
    const px = from.x + t * dx;
    const py = from.y + t * dy;
    const dist = Math.sqrt((point.x - px) ** 2 + (point.y - py) ** 2);
    if (dist >= bestDist) return;

    // Prefer the endpoint when the projection lands next to one
    const distToFrom = Math.sqrt((px - from.x) ** 2 + (py - from.y) ** 2);
    const distToTo = Math.sqrt((px - to.x) ** 2 + (py - to.y) ** 2);
    if (distToFrom < threshold) {
      bestDist = dist;
      best = { x: from.x, y: from.y, snapped: true };
      return;
    }
    if (distToTo < threshold) {
      bestDist = dist;
      best = { x: to.x, y: to.y, snapped: true };
      return;
    }

    bestDist = dist;
    best = { x: px, y: py, snapped: true, segment: { shapeId, edgeIndex, t } };
  };

  for (const shape of shapes) {
    if (shape.id === excludeShapeId) continue;

    if (shape.type === 'wall') {
      const wall = shape as WallShape;
      if (wall.curvature) continue;
      considerSegment(
        { x: wall.x, y: wall.y },
        { x: wall.x2, y: wall.y2 },
        wall.id,
        0
      );
    } else if (shape.type === 'wall-path') {
      const path = shape as WallPathShape;
      const edgeCount = path.closed
        ? path.vertices.length
        : path.vertices.length - 1;
      for (let i = 0; i < edgeCount; i++) {
        if (path.edges[i]?.curvature) continue;
        considerSegment(
          path.vertices[i],
          path.vertices[(i + 1) % path.vertices.length],
          path.id,
          i
        );
      }
    }
  }

  return best;
};
